import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ApiService } from '../../abstract/api.service';
import { Profile } from '../data-models/profile.model';
import { TwoFactorMethod } from '../data-models/two-factor-method.model';
import { LoginFailedResponse } from '../response-models/login-failed-response.model';
import { TwoFactorSetup } from '../response-models/two-factor-setup.model';
import { isRecord } from '../utils/is-record';
import { nullableBoolean, nullableString, omitUndefined, stringArrayOrEmpty } from '../utils/wire-value-utils';

/**
 * The hub's `/auth` routes the admin portal uses (`auth-controller.ts`, `2fa-controller.ts`): the signed-in
 * user's profile, login bookkeeping, password-reset email codes and 2FA. Firebase sign-in itself stays in
 * `AuthService`. Mostly actions, so this is an api-service. Failures arrive as `ApiError`.
 */
@Injectable({ providedIn: 'root' })
export class AuthApiService extends ApiService {
  constructor() {
    super('auth');
  }

  /** GET /auth/me: the signed-in user's profile (`null` when the hub sends none). */
  getMe(): Observable<Profile | null> {
    return this.request('GET', { path: 'me' }, data => (isRecord(data) && isRecord(data['profile']) ? new Profile(data['profile']) : null));
  }

  /** POST /auth/record-login: stamps lastLogin (the platform comes from the interceptor's header). */
  recordLogin(): Observable<void> {
    return this.request('POST', { path: 'record-login' }, () => undefined, {});
  }

  /** POST /auth/login-failed: records a failed login; the result says whether the account is now locked. */
  recordFailedLogin(email: string): Observable<LoginFailedResponse> {
    const read = (data: unknown) => ({ locked: isRecord(data) && nullableBoolean(data['locked']) === true });
    return this.request('POST', { path: 'login-failed' }, read, { email });
  }

  /** POST /auth/email-codes/reset: emails a password-reset code (the hub answers the same for unknown emails). */
  sendPasswordResetCode(email: string): Observable<void> {
    return this.request('POST', { path: ['email-codes', 'reset'] }, () => undefined, { email });
  }

  /** POST /auth/2fa/enable. `phoneNumber` is required for SMS. */
  enableTwoFactor(method: TwoFactorMethod, phoneNumber?: string): Observable<TwoFactorSetup> {
    const body = omitUndefined({ method, phoneNumber });
    return this.request('POST', { path: ['2fa', 'enable'] }, toTwoFactorSetup, body);
  }

  /** POST /auth/2fa/verify: checks a code (or backup code). Fails with a 401 `ApiError` for a wrong code. */
  verifyTwoFactor(code: string, email?: string): Observable<{ backupCodeUsed: boolean }> {
    const read = (data: unknown) => ({ backupCodeUsed: isRecord(data) && data['backupCodeUsed'] === true });
    return this.request('POST', { path: ['2fa', 'verify'] }, read, omitUndefined({ email, code }));
  }

  /** POST /auth/2fa/disable, confirmed with a current code. */
  disableTwoFactor(verificationCode: string): Observable<void> {
    return this.request('POST', { path: ['2fa', 'disable'] }, () => undefined, { verificationCode });
  }

  /** POST /auth/2fa/backup-codes/regenerate: new backup codes (the old ones stop working). */
  regenerateBackupCodes(): Observable<string[]> {
    return this.request('POST', { path: ['2fa', 'backup-codes', 'regenerate'] }, data =>
      isRecord(data) ? stringArrayOrEmpty(data['backupCodes']) : []
    , {});
  }
}

function toTwoFactorSetup(data: unknown): TwoFactorSetup {
  const source = isRecord(data) ? data : {};
  return {
    method: (nullableString(source['method']) ?? '2fa-totp') as TwoFactorMethod,
    qrCode: nullableString(source['qrCode']) ?? null,
    backupCodes: stringArrayOrEmpty(source['backupCodes']),
  };
}
